<script setup lang="ts">
import {
  computed,
  type CSSProperties,
  onMounted,
  reactive,
  useTemplateRef,
  watch,
} from "vue";
import { HideAndShowHelper } from "./hide-and-show-helper";
import { BcTimer } from "./bc-timeout";
import { assertIsDefined } from "./assert-is-defined";

export type BcHideAndShowProps = {
  disableAnimation?: boolean;
};

type AnimationTimes = {
  moveRight: number;
  stayRight: number;
  moveLeft: number;
};

class HideAndShowState {
  isScrolling = false;
  textWidth?: string;
  animationDurations?: AnimationTimes;
  animationDelays?: AnimationTimes;
  delayDM7Timer = new BcTimer(1000);
  scrollDM7Timer = new BcTimer();
}

const hideAndShowClass = "hideAndShow";
const hideAndShowBodyClass = "hideAndShowBody";

const { disableAnimation = false } = defineProps<BcHideAndShowProps>();

const ref_hideAndShowInner = useTemplateRef("ref_hideAndShowInner");
const ref_hideAndShowComponent = useTemplateRef("ref_hideAndShowComponent");
const state = reactive(new HideAndShowState()) as HideAndShowState;

onMounted(() => {
  state.delayDM7Timer.onTimeoutEvent.on(() => (state.isScrolling = true));
  state.scrollDM7Timer.onTimeoutEvent.on(() => (state.isScrolling = false));
});

const scrollStyle = computed<CSSProperties>(() => {
  if (!state.isScrolling) {
    return {};
  }

  const animationDurations = state.animationDurations;
  const animationDelays = state.animationDelays;

  return {
    "--textWidth": `${state.textWidth}`,
    "--animationDelays": `${animationDelays?.moveRight}s, ${animationDelays?.stayRight}s, ${animationDelays?.moveLeft}s`,
    "--animationDurations": `${animationDurations?.moveRight}s, ${animationDurations?.stayRight}s, ${animationDurations?.moveLeft}s`,
  };
});

function startDelayedAutoScroll(): void {
  if (!canStartDelayedAutoScroll()) {
    return;
  }

  state.delayDM7Timer.start();
}

function canStartDelayedAutoScroll(): boolean {
  return (
    ref_hideAndShowComponent.value != null &&
    HideAndShowHelper.isScrollable(ref_hideAndShowComponent.value) &&
    !state.isScrolling
  );
}

function stopDelayedAutoScroll(): void {
  state.delayDM7Timer.stop();
}

function isEllipsisScrollable(): boolean {
  const hideAndShow = ref_hideAndShowInner.value;
  if (!hideAndShow) {
    return false;
  }

  return hideAndShow.scrollWidth === hideAndShow.clientWidth;
}

function getTextWidth(): string {
  if (isEllipsisScrollable()) {
    return `0%`;
  }

  const hideAndShow = ref_hideAndShowInner.value;
  return `calc(${hideAndShow?.clientWidth}px - 100%)`;
}

function getAnimationDurations(): AnimationTimes {
  if (isEllipsisScrollable()) {
    return {
      moveRight: 0,
      stayRight: 1,
      moveLeft: 0,
    };
  }

  const textSpeed = 0.05; // 50 Pixels per second = 0.05 px/millisecond
  const hideAndShow = ref_hideAndShowInner.value;
  assertIsDefined(hideAndShow, "fe1775c0-2840-512c-92b2-91137aab330c");
  const width_px = Math.abs(hideAndShow.offsetWidth - hideAndShow.scrollWidth);

  return {
    moveRight: width_px / textSpeed / 2000, // Making it faster -> /2,
    stayRight: 1, // fixed 1000ms,
    moveLeft: 0.25, // fixed 250ms,
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getAnimationDelays(animationDurations: AnimationTimes) {
  return {
    moveRight: 0,
    stayRight: animationDurations.moveRight,
    moveLeft: animationDurations.moveRight + animationDurations.stayRight,
  };
}

function getTimeoutDuration(
  animationDurations: AnimationTimes,
  animationDelays: AnimationTimes,
): number {
  const duration_ms = animationDurations.moveLeft + animationDelays.moveLeft;
  const duration_s = duration_ms * 1000;
  const offset_s = 0.2;
  return duration_s + offset_s;
}

function onIsScrollingChanged(): void {
  if (!state.isScrolling) {
    return;
  }

  state.textWidth = getTextWidth();
  state.animationDurations = getAnimationDurations();
  state.animationDelays = getAnimationDelays(state.animationDurations);

  const timerDuration = getTimeoutDuration(
    state.animationDurations,
    state.animationDelays,
  );

  state.scrollDM7Timer.start(timerDuration);
}

watch(() => state.isScrolling, onIsScrollingChanged);

defineExpose({
  state,
  hideAndShowClass,
  hideAndShowBodyClass,
  ref_hideAndShowInner,
  ref_hideAndShowComponent,
  scrollStyle,
  startDelayedAutoScroll,
  stopDelayedAutoScroll,
});
</script>

<template>
  <div
    ref="ref_hideAndShowComponent"
    v-bind:class="hideAndShowClass"
    v-if="disableAnimation === true"
  >
    <div ref="ref_hideAndShowInner" class="hideAndShowWrapper">
      <div v-bind:class="hideAndShowBodyClass">
        <slot />
      </div>
    </div>
  </div>
  <div
    v-else
    ref="ref_hideAndShowComponent"
    v-bind:class="hideAndShowClass"
    v-on:mouseenter="startDelayedAutoScroll"
    v-on:mouseleave="stopDelayedAutoScroll"
    v-on:touchstart="startDelayedAutoScroll"
  >
    <div
      ref="ref_hideAndShowInner"
      class="hideAndShowWrapper"
      v-bind:style="scrollStyle"
      v-bind:class="{ autoScroll: state.isScrolling }"
    >
      <div class="hideAndShowBody">
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="less">
@import "../../styles/components/bc-hide-and-show.less";
</style>
